import React from 'react';
import { useContext } from 'react';
import moment from 'moment';
import * as http from 'utils/http';
import config from 'config';
import useSWR from 'swr';
import serialize from 'utils/serialize';
import { getBusinessFilter } from 'utils/filters';
import { LH_EXPRESS_ROUTE_FOR_FILTER, LH_EXPRESS_FR_ROUTE_FOR_FILTER } from 'utils/lhexpress';
import { hasInternationalRouteSelected } from 'utils/international';

const JobReviewServiceContext = React.createContext();

const fetcher = (url) => http.get(`${config.API_URL}${url}`);
const poster = (url, data) => http.post(`${config.API_URL}${url}`, data);

const groupByDate = (driverDays) => {
  return driverDays?.reduce((acc, driverDay) => {
    const { start_time_from } = driverDay;
    const formattedDate = moment(start_time_from).format('YYYY-MM-DD');
    return {
      ...acc,
      [formattedDate]: [...(acc[formattedDate] || []), driverDay],
    };
  }, {});
};

const useWithFilters = (url, filters) => {
  const { date, route, vehicle_type } = filters;
  const dateFrom = date?.from;
  const dateTo = date?.to;

  const isInternational = hasInternationalRouteSelected(route);
  const isLhEs = (route || []).some((r) => r.value === LH_EXPRESS_ROUTE_FOR_FILTER.name);
  const isLhFr = (route || []).some((r) => r.value === LH_EXPRESS_FR_ROUTE_FOR_FILTER.name);
  const regularRoutes = (route || []).filter(
    (r) =>
      ![LH_EXPRESS_ROUTE_FOR_FILTER.name, LH_EXPRESS_FR_ROUTE_FOR_FILTER.name].includes(r.value)
  );

  const routeFilter = isInternational
    ? undefined
    : regularRoutes.map((route) => `${route.value}->${route.value}`).join(',');

  const requestParams = {
    date_from: dateFrom,
    date_to: dateTo,
    route: routeFilter,
    vehicle_type: vehicle_type
      ?.map((vehicle_type) => `${vehicle_type.value}`.toLowerCase())
      .join(','),
    page_size: 0,
    international: isInternational || undefined,
    long_haul: isLhEs || undefined,
    long_haul_fr: isLhFr || undefined,
    ...getBusinessFilter(filters?.business, 'has_ftl_shipper_order'),
  };

  return `${url}/?${serialize(requestParams)}`;
};

const useDriverDayFiltered = (swrOptions) => {
  const { filters } = useContext(JobReviewServiceContext);

  const url = useWithFilters('driver-day', filters);

  const { data: driverDays, mutate } = useSWR(url, fetcher, swrOptions);

  const groupedByDate = groupByDate(driverDays);

  return {
    driverDayGrouped: groupedByDate,
    mutate,
  };
};

const useDriverDayCounterFiltered = (swrOptions) => {
  const { filters } = useContext(JobReviewServiceContext);

  const url = useWithFilters('driver-day/count', filters);

  const { data: count } = useSWR(url, fetcher, swrOptions);

  const countData = count;
  const countTotal = Object.keys(count || {}).reduce((acc, date) => acc + countData[date], 0);

  return { countData, countTotal };
};

const useDriverDayDetail = (id, swrOptions) => {
  const { data: driverDay, error, mutate } = useSWR(`driver-day/${id}/`, fetcher, swrOptions);

  return {
    driverDay,
    error,
    mutate,
  };
};

const reviewDriverDay = async (id) => {
  return await poster(`driver-day/${id}/review/`);
};

const unassignJourney = async (journeyId) => {
  return await http.put(`${config.API_URL}mercury/journeys/${journeyId}/unassign/`, {
    launch_offer: false,
  });
};

export {
  useDriverDayFiltered,
  useDriverDayDetail,
  useDriverDayCounterFiltered,
  reviewDriverDay,
  JobReviewServiceContext,
  unassignJourney,
};
